/**
 * Конвертер Suno `response_data` (JSON с инструментами и нотами) в бинарный .mid.
 *
 * По гайду §10: бэк сохраняет MIDI как JSON со структурой
 *   { instruments: [{ name, program, is_drum, notes: [{ pitch, start, end, velocity }] }] }
 *
 * Времена (start/end) — в секундах. Конвертируем в тики при 120 BPM / 128 PPQN
 * (стандартные дефолты midi-writer-js). Темп кладём в каждый трек.
 */
// @ts-expect-error — у пакета package.json "exports" не резолвит types правильно,
// но runtime import работает. Внутренние API не типизируем строго — просто any.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import MidiWriter from 'midi-writer-js'

interface SunoMidiNote {
	pitch: number
	start: number
	end: number
	velocity?: number
}

export interface SunoMidiInstrument {
	name?: string
	program?: number
	is_drum?: boolean
	notes?: SunoMidiNote[]
}

const BPM = 120
const PPQN = 128 // ticks per quarter note — дефолт midi-writer-js
const TICKS_PER_SECOND = (BPM / 60) * PPQN // = 256

/**
 * Парсит `response_data` в унифицированный массив инструментов. Толерантно
 * к разным shape'ам от Suno / нашего worker'а:
 *   - {instruments: [...]}
 *   - {tracks: [...]}
 *   - [...]                               (плоский массив)
 *   - {midi_data: {instruments: [...]}}   (наш worker сохраняет так)
 *   - {raw: {midi_data: {...}}}           (unwrap если upstream обернул)
 *   - {data: {response: {...}}}           (legacy shape Suno)
 */
export function parseMidiResponseData(data: unknown): SunoMidiInstrument[] | null {
	if (!data) return null
	// Плоский массив — сразу.
	if (Array.isArray(data)) return data as SunoMidiInstrument[]
	if (typeof data !== 'object') return null

	// Рекурсивно проверяем кандидаты, идя по наиболее вероятным ключам.
	const candidates: unknown[] = [data]
	const seen = new Set<unknown>()
	while (candidates.length > 0) {
		const current = candidates.shift()
		if (!current || typeof current !== 'object' || seen.has(current)) continue
		seen.add(current)

		const d = current as Record<string, unknown>

		if (Array.isArray(d.instruments)) return d.instruments as SunoMidiInstrument[]
		if (Array.isArray(d.tracks)) return d.tracks as SunoMidiInstrument[]

		// Обёртки — добавляем вложенное в очередь проверки.
		for (const key of ['midi_data', 'midiData', 'raw', 'data', 'response']) {
			if (d[key] && typeof d[key] === 'object') candidates.push(d[key])
		}
	}

	return null
}

function secondsToTicks(s: number): number {
	return Math.max(0, Math.round(s * TICKS_PER_SECOND))
}

/**
 * Собирает .mid-файл из массива инструментов и возвращает `Uint8Array`.
 * Возвращает `null` если во всех инструментах нет валидных нот.
 */
export function buildMidiFile(instruments: SunoMidiInstrument[]): Uint8Array | null {
	const tracks: unknown[] = []

	for (const inst of instruments) {
		const notes = (inst.notes ?? []).filter(
			(n) =>
				Number.isFinite(n.pitch) &&
				Number.isFinite(n.start) &&
				Number.isFinite(n.end) &&
				n.end > n.start,
		)
		if (notes.length === 0) continue

		// Сортируем по времени начала — midi-writer-js строит трек последовательно.
		notes.sort((a, b) => a.start - b.start)

		const track = new MidiWriter.Track()
		track.setTempo(BPM)
		if (inst.name) track.addTrackName(inst.name)
		if (typeof inst.program === 'number' && !inst.is_drum) {
			track.addEvent(new MidiWriter.ProgramChangeEvent({ instrument: inst.program }))
		}

		let cursorTicks = 0
		for (const n of notes) {
			const startTicks = secondsToTicks(n.start)
			const endTicks = secondsToTicks(n.end)
			const durTicks = Math.max(1, endTicks - startTicks)
			const waitTicks = Math.max(0, startTicks - cursorTicks)

			const noteEvent = new MidiWriter.NoteEvent({
				pitch: [n.pitch],
				duration: `T${durTicks}`,
				wait: waitTicks > 0 ? `T${waitTicks}` : '0',
				velocity: Math.max(1, Math.min(100, Math.round((n.velocity ?? 80)))),
				channel: inst.is_drum ? 10 : undefined,
			})
			track.addEvent(noteEvent)
			cursorTicks = endTicks
		}

		tracks.push(track)
	}

	if (tracks.length === 0) return null

	const writer = new MidiWriter.Writer(tracks as never)
	return writer.buildFile()
}

/** Скачивание .mid как файла — утилита для компонентов. */
export function downloadMidi(bytes: Uint8Array, filename: string): void {
	// Uint8Array.buffer может быть SharedArrayBuffer — Blob требует ArrayBuffer.
	// Явно копируем в новый ArrayBuffer через slice, чтобы TS не ругался.
	const ab = bytes.slice().buffer as ArrayBuffer
	const blob = new Blob([ab], { type: 'audio/midi' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename.endsWith('.mid') ? filename : `${filename}.mid`
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(url), 10000)
}
